import {
  Body,
  Button,
  Group,
  H7,
  MaterialIcon,
  Stack,
} from '@qred/components-library';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ValidationContext } from '~/components/hoc/withValidation';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import TrustpilotWidget from '~/components/shared/TrustpilotWidget/TrustpilotWidget';
import constVars from '~/constants/constVars';
import { marketHasTrustpilot } from '~/constants/markets';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { getEskatIntegrationUrl } from '~/store/actions/eskatActions';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { patchApplication } from '~/store/actions/loanApplicationActions';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { LastBasicStepSummaryBox } from '../Steps/LastBasicStepSummaryBox';
import useGoToNextStep from '../Steps/useGoToNextStep';

const ConnectToEskat = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {
    loanApplication: {
      form: { qCode, applicationUuid, eskatConnected },
      apiStatus: applicationApiStatus,
    },
    eskat,
    matchedMedia: { mobile },
    intl: { market },
  } = useSelector((state: RootState) => state);
  const onNextStepClick = useGoToNextStep();

  const validationContext = useContext(ValidationContext);

  const connectToEskatOnClickHandler = () => {
    if (qCode && applicationUuid) {
      dispatch(
        pushToGtmLoanAction({
          actionName: 'eskat_connect_pressed',
        })
      );
      sessionStorage.setItem('loan-application-uuid', applicationUuid);
      dispatch(getEskatIntegrationUrl(qCode));
    }
  };

  useEffect(() => {
    if (!eskatConnected) {
      validationContext.addPropertyToValidationErrors('eskatConnected');
    }

    return () => {
      validationContext.removePropertyFromValidationErrors('eskatConnected');
    };
  }, [eskatConnected]);

  const isTestEnvironment = ['development', 'test'].includes(
    import.meta.env.MODE
  );

  const emulateSuccessfulConnection = () => {
    if (qCode && applicationUuid) {
      sessionStorage.setItem('loan-application-uuid', applicationUuid);
      history.push(`/${constVars.ROUTE_ESKAT}/redirected`);
    }
  };

  const revertEskatConnection = () => {
    dispatch(updateForm({ eskatConnected: false }));
    dispatch(patchApplication({ currentStepCompleted: false }));
  };

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <StepHeader
          title="Onboarding.TaxReport"
          description="Onboarding.EskatText"
        />
        <Stack mt="xl" spacing="lg" align={mobile ? 'stretch' : 'center'}>
          {eskatConnected ? (
            <Group align="center" spacing={'sm'}>
              <MaterialIcon src="check_circle" />
              <H7>{t('Onboarding.EskatSuccessText')}</H7>
            </Group>
          ) : (
            <Stack spacing="sm">
              <Button
                variant="primary"
                size="md"
                dataCy="connect_to_eskat_button"
                onClick={connectToEskatOnClickHandler}
                isLoading={eskat.apiStatus === ApiStatus.Started}
                fullWidth={mobile}
                mt={5}
              >
                {t('Onboarding.ConnectToEskat')}
              </Button>
            </Stack>
          )}

          {isTestEnvironment && (
            <>
              {eskatConnected ? (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={revertEskatConnection}
                  isLoading={eskat.apiStatus === ApiStatus.Started}
                  fullWidth={mobile}
                  mt={5}
                >
                  Revert eskat connection
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={emulateSuccessfulConnection}
                  isLoading={eskat.apiStatus === ApiStatus.Started}
                  fullWidth={mobile}
                  mt={5}
                >
                  Emulate Successful Connection
                </Button>
              )}
            </>
          )}
        </Stack>
        {validationContext.isFormValid && <LastBasicStepSummaryBox />}
      </StandardStepContentBody>
      <StandardStepContentFooter>
        <NextButton
          label="Onboarding.Submit"
          isLoading={applicationApiStatus.patch === ApiStatus.Started}
          action={onNextStepClick}
        />
        <Stack spacing={'sm'}>
          {marketHasTrustpilot[market] && (
            <TrustpilotWidget templateId="5419b637fa0340045cd0c936" />
          )}
          <Body size="sm" display="block">
            {t('Onboarding.ApplicationProcessingConsent')}
          </Body>
        </Stack>
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};

export default ConnectToEskat;
